import { theme } from '@theme';
import { ChangeEvent, Dispatch, FC, SetStateAction, useState } from 'react';
import { isAuth0 } from '@services/auth/AuthService';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useCheckDevice } from '../../../../hooks/UseCheckDevice';
import { PasswordValidationResponse } from '../../../../types/header/PasswordValidationResponse';
import { UserProfileFormResource } from '../../../../types/header/UserProfile';
import {
  IconDealerUser,
  IconPrimaryUser,
  RowDivider,
  RowDividerChangePassword,
} from '../UserProfileStyles';
import UserProfileChangePassword from './UserProfileChangePassword';
import { useConfig } from '@hooks/UseConfig';

type UserProfilePasswordProps = {
  isPrimaryUser: boolean;
  isIncentiveAdvantageType: boolean;
  showChangePassword: boolean;
  currentPasswordIncorrect: boolean;
  invalidPassword: boolean;
  submitted: boolean;
  passwordValidationResponse: PasswordValidationResponse;
  showChangePasswordToggle: () => void;
  setCurrentPasswordIncorrect: Dispatch<SetStateAction<boolean>>;
  setPasswordValidationResponse: Dispatch<SetStateAction<PasswordValidationResponse>>;
};

const UserProfilePassword: FC<UserProfilePasswordProps> = (props) => {
  const {
    isPrimaryUser,
    isIncentiveAdvantageType,
    showChangePassword,
    invalidPassword,
    submitted,
    passwordValidationResponse,
    currentPasswordIncorrect,
    showChangePasswordToggle,
    setPasswordValidationResponse,
    setCurrentPasswordIncorrect,
  } = props;

  const { register, control, getValues } = useFormContext<UserProfileFormResource>();

  const { stsAuthority } = useConfig();
  const [newPassword, setNewPassword] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<boolean>(false);
  const [newPasswordDealer, setNewPasswordDealer] = useState<boolean>(false);
  const [confirmPasswordDealer, setConfirmPasswordDealer] = useState<boolean>(false);

  const passwordField =
    isPrimaryUser || isIncentiveAdvantageType
      ? getValues('password')
      : getValues('newPasswordDealer');
  const confirmPasswordField =
    isPrimaryUser || isIncentiveAdvantageType
      ? getValues('confirmPassword')
      : getValues('confirmPasswordDealer');

  const passwordRequiredFailed = !passwordField;
  const currentPasswordRequiredFailed = !getValues('currentPassword');
  const confirmPasswordRequiredFailed = !confirmPasswordField;
  const passwordNotMatch =
    confirmPasswordField.length > 0 ? passwordField !== confirmPasswordField : false;

  const { isMobileDevice } = useCheckDevice();

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>, onChange: any): void => {
    onChange(event);
    setTimeout(() => {
      const passwordField =
        isPrimaryUser || isIncentiveAdvantageType
          ? getValues('password')
          : getValues('newPasswordDealer');
      validatePassword(passwordField);

      if (!isPrimaryUser) {
        if (currentPasswordIncorrect) {
          setCurrentPasswordIncorrect(false);
        }
      }
    }, 1);
  };

  const validatePassword = (password: string): void => {
    const passwordValidationResponse = {
      minLength: password && password.length >= 8,
      lowerCase: password.search(/[a-z]/) > -1,
      upperCase: password.search(/[A-Z]/) > -1,
      specialCharacter: password.search(/[!?$@&#_%.-]/) > -1,
      number: password.search(/[0-9]/) > -1,
      invalidSpecialCharacter: password.search(/[`~\\^*()+=|{}[\]'":;\/<>,]/) > -1,
    };

    setPasswordValidationResponse(passwordValidationResponse);
  };

  const toggleShowNewPassword = (): void => {
    setNewPassword(!newPassword);
  };

  const toggleShowConfirmPassword = (): void => {
    setConfirmPassword(!confirmPassword);
  };

  const toggleCurrentPassword = (): void => {
    setCurrentPassword(!currentPassword);
  };

  const toggleNewPasswordDealer = (): void => {
    setNewPasswordDealer(!newPasswordDealer);
  };

  const toggleConfirmPasswordDealer = (): void => {
    setConfirmPasswordDealer(!confirmPasswordDealer);
  };

  return (
    <>
      {(isPrimaryUser || isIncentiveAdvantageType) && !isAuth0(stsAuthority) && (
        <RowDivider
          borderColor={isMobileDevice ? theme.palette.backgroundGray : theme.palette.lightGreySix}
        >
          <div className='row mb-3'>
            <div className='col-sm-6 col-xs-12'>
              <LabelWrapper>New Password</LabelWrapper>
              <div>
                <PasswordWrapper>
                  <Controller
                    control={control}
                    name={'password'}
                    render={({ field: { onChange, ...field } }) => {
                      return (
                        <input
                          {...field}
                          className={`form-control ${!newPassword ? 'password-protect' : ''} ${
                            (invalidPassword ||
                              passwordRequiredFailed ||
                              (passwordValidationResponse &&
                                passwordValidationResponse.invalidSpecialCharacter)) &&
                            submitted
                              ? 'error no-margin'
                              : ''
                          } `}
                          type='text'
                          autoFocus={!isMobileDevice}
                          id='newPassword'
                          name='newPassword'
                          autoComplete='off'
                          onChange={(e) => onPasswordChange(e, onChange)}
                        />
                      );
                    }}
                  />
                  {!newPassword && (
                    <IconWrapper right='10'>
                      <IconPrimaryUser type='eye-full-crossed' onClick={toggleShowNewPassword} />
                    </IconWrapper>
                  )}
                  {newPassword && (
                    <IconWrapper right='10'>
                      <IconPrimaryUser type='eye-full' onClick={toggleShowNewPassword} />
                    </IconWrapper>
                  )}
                </PasswordWrapper>
                <PasswordValidationWrapper>
                  {submitted && passwordRequiredFailed && (
                    <p className='error'>Password Required</p>
                  )}
                  {submitted && invalidPassword && (
                    <p className='error'>Password must meet requirements</p>
                  )}
                  {passwordValidationResponse && (
                    <>
                      {passwordValidationResponse.invalidSpecialCharacter && (
                        <p className='error'>Special characters allowed _ . - ! @ $ ? &amp; % #</p>
                      )}
                      <p className={passwordValidationResponse.minLength ? 'success' : 'info'}>
                        {passwordValidationResponse.minLength && (
                          <i className='am-icon-check-circle green' />
                        )}
                        {!passwordValidationResponse.minLength && (
                          <i className='am-icon-warning grey' />
                        )}
                        <span> At least 8 characters</span>
                      </p>
                      <p className={passwordValidationResponse.upperCase ? 'success' : 'info'}>
                        {passwordValidationResponse.upperCase && (
                          <i className='am-icon-check-circle green' />
                        )}
                        {!passwordValidationResponse.upperCase && (
                          <i className='am-icon-warning grey' />
                        )}
                        <span> At least 1 Upper Case Letter</span>
                      </p>
                      <p className={passwordValidationResponse.lowerCase ? 'success' : 'info'}>
                        {passwordValidationResponse.lowerCase && (
                          <i className='am-icon-check-circle green' />
                        )}
                        {!passwordValidationResponse.lowerCase && (
                          <i className='am-icon-warning grey' />
                        )}
                        <span> At least 1 Lower Case Letter</span>
                      </p>
                      <p className={passwordValidationResponse.number ? 'success' : 'info'}>
                        {passwordValidationResponse.number && (
                          <i className='am-icon-check-circle green' />
                        )}
                        {!passwordValidationResponse.number && (
                          <i className='am-icon-warning grey' />
                        )}
                        <span> At least 1 Number</span>
                      </p>
                      <p
                        className={passwordValidationResponse.specialCharacter ? 'success' : 'info'}
                      >
                        {passwordValidationResponse.specialCharacter && (
                          <i className='am-icon-check-circle green' />
                        )}
                        {!passwordValidationResponse.specialCharacter && (
                          <i className='am-icon-warning grey' />
                        )}
                        <span> At least 1 Special Character</span>
                      </p>
                    </>
                  )}
                </PasswordValidationWrapper>
              </div>
            </div>
            <div className='col-sm-6 col-xs-12'>
              <LabelWrapper>Re-type Password</LabelWrapper>
              <div>
                <PasswordWrapper>
                  <Controller
                    control={control}
                    name={'confirmPassword'}
                    render={({ field: { onChange, ...field } }) => {
                      return (
                        <input
                          {...field}
                          className={`form-control ${!confirmPassword ? 'password-protect' : ''} ${passwordNotMatch && submitted ? 'error no-margin' : ''}`}
                          type='text'
                          id='confirmPassword'
                          name='confirmPassword'
                          autoComplete='off'
                          onChange={(e) => onPasswordChange(e, onChange)}
                        />
                      );
                    }}
                  />
                  {!confirmPassword && (
                    <IconWrapper right='15'>
                      <IconPrimaryUser
                        type='eye-full-crossed'
                        onClick={toggleShowConfirmPassword}
                      />{' '}
                    </IconWrapper>
                  )}
                  {confirmPassword && (
                    <IconWrapper right='15'>
                      <IconPrimaryUser type='eye-full' onClick={toggleShowConfirmPassword} />
                    </IconWrapper>
                  )}
                </PasswordWrapper>
                <PasswordValidationWrapper>
                  {passwordNotMatch && <p className='error'>Passwords must match</p>}
                </PasswordValidationWrapper>
              </div>
            </div>
          </div>
        </RowDivider>
      )}
      {!isPrimaryUser && (
        <RowDividerChangePassword
          borderColor={isMobileDevice ? theme.palette.backgroundGray : theme.palette.lightGreySix}
        >
          {isMobileDevice && (
            <ChangePasswordWrapper>
              <UserProfileChangePassword
                isIncentiveAdvantageType={isIncentiveAdvantageType}
                isPrimaryUser={isPrimaryUser}
                showChangePassword={showChangePassword}
                showChangePasswordToggle={showChangePasswordToggle}
              />
            </ChangePasswordWrapper>
          )}
          {showChangePassword && (
            <div className='form-horizontal'>
              <div className='col-sm-12 col-xs-12'>
                <label className='control-label m-l-n5 change-password'>Change Password</label>
                <div className='form-group pb-5 mb-0'>
                  <div className='col-sm-4 col-xs-12'>
                    <LabelWithoutPaddingWrapper>Current Password *</LabelWithoutPaddingWrapper>
                    <div>
                      <PasswordWrapper>
                        <input
                          className={`form-control ${!currentPassword ? 'password-protect' : ''} ${currentPasswordRequiredFailed && submitted ? 'error no-margin' : ''}`}
                          type='text'
                          autoFocus={!isMobileDevice}
                          id='currentPassword'
                          name='currentPassword'
                          autoComplete='off'
                          {...register('currentPassword')}
                        />
                        {!currentPassword && (
                          <IconWrapper>
                            <IconDealerUser
                              type='eye-full-crossed'
                              onClick={toggleCurrentPassword}
                            />
                          </IconWrapper>
                        )}
                        {currentPassword && (
                          <IconWrapper>
                            <IconDealerUser type='eye-full' onClick={toggleCurrentPassword} />
                          </IconWrapper>
                        )}
                      </PasswordWrapper>
                      <PasswordValidationWrapper>
                        {submitted && currentPasswordRequiredFailed && (
                          <p className='error'>Current Password Required</p>
                        )}
                        {showChangePassword && currentPasswordIncorrect && (
                          <p className='error'>Please make sure the current password is correct</p>
                        )}
                      </PasswordValidationWrapper>
                    </div>
                  </div>
                  <div className='col-sm-4 col-xs-12'>
                    <LabelWithoutPaddingWrapper>New Password *</LabelWithoutPaddingWrapper>
                    <div>
                      <PasswordWrapper>
                        <Controller
                          control={control}
                          name={'newPasswordDealer'}
                          render={({ field: { value, onChange, ...field } }) => {
                            return (
                              <input
                                {...field}
                                className={`form-control ${!newPasswordDealer ? 'password-protect' : ''} ${
                                  (passwordRequiredFailed ||
                                    invalidPassword ||
                                    (passwordValidationResponse &&
                                      passwordValidationResponse.invalidSpecialCharacter)) &&
                                  submitted
                                    ? 'error no-margin'
                                    : ''
                                }`}
                                type='text'
                                id='newPasswordDealer'
                                name='newPasswordDealer'
                                autoComplete='off'
                                onChange={(e) => onPasswordChange(e, onChange)}
                              />
                            );
                          }}
                        />
                        {!newPasswordDealer && (
                          <IconWrapper>
                            <IconDealerUser
                              type='eye-full-crossed'
                              onClick={toggleNewPasswordDealer}
                            />
                          </IconWrapper>
                        )}
                        {newPasswordDealer && (
                          <IconWrapper>
                            <IconDealerUser type='eye-full' onClick={toggleNewPasswordDealer} />
                          </IconWrapper>
                        )}
                      </PasswordWrapper>
                      <PasswordValidationWrapper>
                        {submitted && passwordRequiredFailed && (
                          <p className='error'>Password Required</p>
                        )}
                        {submitted && invalidPassword && (
                          <p className='error'>Password must meet requirements</p>
                        )}
                        {passwordValidationResponse && (
                          <>
                            {passwordValidationResponse.invalidSpecialCharacter && (
                              <p className='error'>
                                Special characters allowed _ . - ! @ $ ? &amp; % #
                              </p>
                            )}
                            <p
                              className={passwordValidationResponse.minLength ? 'success' : 'info'}
                            >
                              {passwordValidationResponse.minLength && (
                                <i className='am-icon-check-circle green' />
                              )}
                              {!passwordValidationResponse.minLength && (
                                <i className='am-icon-warning grey' />
                              )}
                              <span> At least 8 characters</span>
                            </p>
                            <p
                              className={passwordValidationResponse.upperCase ? 'success' : 'info'}
                            >
                              {passwordValidationResponse.upperCase && (
                                <i className='am-icon-check-circle green' />
                              )}
                              {!passwordValidationResponse.upperCase && (
                                <i className='am-icon-warning grey' />
                              )}
                              <span> At least 1 Upper Case Letter</span>
                            </p>
                            <p
                              className={passwordValidationResponse.lowerCase ? 'success' : 'info'}
                            >
                              {passwordValidationResponse.lowerCase && (
                                <i className='am-icon-check-circle green' />
                              )}
                              {!passwordValidationResponse.lowerCase && (
                                <i className='am-icon-warning grey' />
                              )}
                              <span> At least 1 Lower Case Letter</span>
                            </p>
                            <p className={passwordValidationResponse.number ? 'success' : 'info'}>
                              {passwordValidationResponse.number && (
                                <i className='am-icon-check-circle green' />
                              )}
                              {!passwordValidationResponse.number && (
                                <i className='am-icon-warning grey' />
                              )}
                              <span> At least 1 Number</span>
                            </p>
                            <p
                              className={
                                passwordValidationResponse.specialCharacter ? 'success' : 'info'
                              }
                            >
                              {passwordValidationResponse.specialCharacter && (
                                <i className='am-icon-check-circle green' />
                              )}
                              {!passwordValidationResponse.specialCharacter && (
                                <i className='am-icon-warning grey' />
                              )}
                              <span> At least 1 Special Character</span>
                            </p>
                          </>
                        )}
                      </PasswordValidationWrapper>
                    </div>
                  </div>
                  <div className='col-sm-4 col-xs-12'>
                    <LabelWithoutPaddingWrapper>Confirm New Password *</LabelWithoutPaddingWrapper>
                    <div>
                      <PasswordWrapper>
                        <Controller
                          control={control}
                          name={'confirmPasswordDealer'}
                          render={({ field: { value, onChange, ...field } }) => {
                            return (
                              <input
                                {...field}
                                className={`form-control ${!confirmPasswordDealer ? 'password-protect' : ''} ${
                                  (passwordNotMatch || confirmPasswordRequiredFailed) && submitted
                                    ? 'error no-margin'
                                    : ''
                                } `}
                                type='text'
                                id='confirmPasswordDealer'
                                name='confirmPasswordDealer'
                                autoComplete='off'
                                onChange={(e) => onPasswordChange(e, onChange)}
                              />
                            );
                          }}
                        />
                        {!confirmPasswordDealer && (
                          <IconWrapper>
                            <IconDealerUser
                              type='eye-full-crossed'
                              onClick={toggleConfirmPasswordDealer}
                            />
                          </IconWrapper>
                        )}
                        {confirmPasswordDealer && (
                          <IconWrapper>
                            <IconDealerUser type='eye-full' onClick={toggleConfirmPasswordDealer} />
                          </IconWrapper>
                        )}
                      </PasswordWrapper>
                      <PasswordValidationWrapper>
                        {submitted && passwordNotMatch && (
                          <p className='error'>Passwords must match</p>
                        )}
                        {submitted && confirmPasswordRequiredFailed && (
                          <p className='error'>Confirm password required</p>
                        )}
                      </PasswordValidationWrapper>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </RowDividerChangePassword>
      )}
    </>
  );
};

export default UserProfilePassword;
type IconWrapperProps = {
  right?: string;
};

const IconWrapper = styled.span<IconWrapperProps>`
  z-index: 9999;
  position: absolute;
  top: 15%;
  right: ${(props) => (props.right ? props.right : '5')}px;
`;

const PasswordWrapper = styled.div`
  position: relative;
  .error {
    border: 1px solid ${theme.palette.danger} !important;
  }
`;

const PasswordValidationWrapper = styled.div`
  margin-top: 5px;
  p {
    margin: 5px 0 0;
    font-size: ${theme.typography.fontSize14}px;
    word-break: break-word;
  }
  .error {
    color: ${theme.palette.danger};
  }
  .info {
    color: ${theme.palette.dustyTwo};
    margin-bottom: 0;
  }
  .success {
    color: ${theme.palette.success};
  }
`;

const LabelWrapper = styled.label`
  color: ${theme.palette.wetAsphalt};
  white-space: nowrap;
  margin: 7px 0;
  font-size: ${theme.typography.fontSize14}px;

  @media (min-width: 768px) {
    padding-top: 7px;
    text-align: right;
  }
`;

const LabelWithoutPaddingWrapper = styled.label`
  padding-top: 0;
  font-size: ${theme.typography.fontSize14}px;
`;
const ChangePasswordWrapper = styled.div`
  padding: 0px 10px;
`;
